import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Typography, IconButton, Switch, FormControlLabel, Box, Slide
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { postData, putData } from "../../BaseUrl";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CATEGORIES = ["Planning", "General"];

const SUBJECTS = [
  "Tamil Reading",
  "Tamil Writing",
  "Tamil Grammar",
  "Speaking",
  "Mathematics",
  "Science",
  "English",
  "Social Studies",
  "Arts",
  "Music"
];

const ASSIGN_TO_OPTIONS = ["Teacher", "Student", "Admin", "All"];

const GuidelineFormDialog = ({ open, onClose, guideline, onSuccess }) => {
  const [form, setForm] = useState({
    title: "",
    description: "",
    gradeSection: "",
    category: "",
    subject: "",
    assignTo: "",
    published: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isEdit = Boolean(guideline);

  useEffect(() => {
    if (guideline) {
      setForm({
        title: guideline.title || "",
        description: guideline.description || "",
        gradeSection: guideline.gradeSection || guideline.grade || "",
        category: guideline.category || "",
        subject: guideline.subject || "",
        assignTo: guideline.assignTo || "",
        published: guideline.status === "published" || guideline.published || false
      });
    } else {
      setForm({
        title: "", description: "", gradeSection: "", category: "",
        subject: "", assignTo: "", published: true
      });
    }
    setError("");
  }, [guideline, open]);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.title || !form.category) {
      setError("Please fill all required fields");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...form,
        grade: form.gradeSection,
        status: form.published ? "published" : "unpublished"
      };

      if (isEdit) {
        await putData(`admin/project-guidelines/${guideline.id}`, payload);
        onSuccess("Guideline updated successfully");
      } else {
        await postData("admin/project-guidelines/", payload);
        onSuccess("Guideline created successfully");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Operation failed");
    }
    setLoading(false);
  };

  const handleClose = () => {
    setForm({
      title: "", description: "", gradeSection: "", category: "",
      subject: "", assignTo: "", published: true
    });
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={Transition}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight="bold">
          {isEdit ? "Edit Guideline" : "Create Guideline"}
        </Typography>
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField label="Title *" fullWidth size="small"
              value={form.title} onChange={handleChange("title")} />
          </Grid>

          {/* Subject dropdown */}
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Subject</InputLabel>
              <Select value={form.subject} label="Subject" onChange={handleChange("subject")}>
                {SUBJECTS.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Assign To dropdown */}
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Assign To</InputLabel>
              <Select value={form.assignTo} label="Assign To" onChange={handleChange("assignTo")}
                displayEmpty renderValue={(val) => val || ""}>
                {ASSIGN_TO_OPTIONS.map(o => (
                  <MenuItem key={o} value={o}>{o}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Grade & Section combined */}
          <Grid item xs={6}>
            <TextField
              label="Grade & Section"
              fullWidth size="small"
              placeholder="Nilai08 A"
              value={form.gradeSection}
              onChange={handleChange("gradeSection")}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Category *</InputLabel>
              <Select value={form.category} label="Category *" onChange={handleChange("category")}>
                {CATEGORIES.map(c => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Description with hint text */}
          <Grid item xs={12}>
            <TextField label="Description" fullWidth size="small" multiline rows={4}
              value={form.description} onChange={handleChange("description")} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Briefly describe the project role and its purpose
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", p: 1.5, borderRadius: 2, backgroundColor: "#f5f5f5" }}>
              <FormControlLabel
                control={
                  <Switch checked={form.published}
                    onChange={(e) => setForm(prev => ({ ...prev, published: e.target.checked }))}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" } }} />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    {form.published ? "Published" : "Unpublished"}
                  </Typography>
                }
              />
            </Box>
          </Grid>
        </Grid>
        {error && <Typography color="error" variant="body2" mt={2}>{error}</Typography>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit"
          sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}
          sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", textTransform: "none" }}>
          {loading ? "Saving..." : isEdit ? "Update" : "Publish"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GuidelineFormDialog;
